import { useSpring } from '@react-spring/web'
import { createUseGesture, dragAction, pinchAction, wheelAction } from '@use-gesture/react'
import { clone } from 'lodash'
import { isMobile } from 'mobile-device-detect'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import logger from '../../../utils/logger'

interface Bounds {
  left: number
  right: number
  top: number
  bottom: number
}

// Determines how much white space an image can have before being pulled back towards
// the center of the screen. Prevents user from moving image off the screen.
const dragRubberband = isMobile ? -0.15 : 0.15
const useGesture = createUseGesture([dragAction, pinchAction, wheelAction])

const defaultImageState = {
  x: 0,
  y: 0,
  scale: 1,
  rotateZ: 0
}

export default function usePinchZoom(bounds?: Bounds) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const zoom = Number(params.get('zoom') || 1)
  const [style, api] = useSpring(() => clone(defaultImageState))
  const scaleRef = useRef(style.scale)
  const ref = useRef<HTMLImageElement | null>(null)
  const scaleBounds = { min: 0.5, max: 3 }

  useEffect(() => {
    if (zoom > 0 && zoom <= 10) {
      scaleRef.current.set(zoom)
    }
  }, [zoom])

  useGesture(
    {
      onDrag: ({ elapsedTime, pinching, offset: [x, y] }) => {
        if (pinching) {
          return undefined
        }

        if (elapsedTime < 250) {
          return undefined
        }

        return api.start({ x, y })
      },
      onDragEnd: ({ pinching, elapsedTime }) => {
        if (pinching) {
          return undefined
        }

        if (elapsedTime < 250) {
          navigate(`${location.pathname}?action=focus`)
        }
        return undefined
      },
      onPinch: ({ offset: [scale] }) => {
        if (!isMobile) {
          return
        }
        if (!ref.current) {
          logger.error('Invalid imgRef')
          return
        }
        style.scale.set(scale)
      },
      onWheel: ({ delta: [, dy] }) => {
        // works better to manually manage range for scale using wheel actions than the configs below
        let newScale = Math.max(0.5, style.scale.get() - 0.01 * dy)
        newScale = Math.min(3, newScale)
        api.start({ scale: newScale })
      }
    },
    {
      target: ref,
      drag: {
        from: () => [style.x.get(), style.y.get()],
        bounds,
        rubberband: dragRubberband
      },
      pinch: {
        scaleBounds,
        bounds,
        rubberband: true
      },
      wheel: {
        from: () => [style.x.get(), style.y.get()],
        bounds,
        scaleBounds
      }
    }
  )

  return { ref, style }
}
